import { Attendance } from '../types/attendance';
import axiosInstance from '../api/apiInterceptor';
import { AxiosResponse } from 'axios';
import {
  Attendance_API_ENDPOINT,
  ATTENDANE_API_ENDPOINT_STUDENT_ATTENDANCE,
  ATTENDANE_API_ENDPOINT_STUDENT_ATTENDANCE_MULTIPLE
} from "../../../constants/constants";

// FIXED: Made classSectionId optional to support school-wide queries
interface AttendancePayload {
  classSectionId?: number | null;
  attnDate: string; 
  schoolId?: number; 
  limit?: number;
}

interface AttendanceSubmissionPayload {
  attendances: {
    studentId: number;
    schoolId: number;
    classSectionId: number;
    attnDate: string;
    attendance: boolean;
    attnTypeId: number;
    recordedVia: number;
    fileReference: {
      url: string;
    };
    createdBy: number;
  }[];
}

// Extract error message helper
const extractErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error?.response?.data?.details) {
    return error.response.data.details;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Custom API Error class
class ApiError extends Error {
  public originalError: any;
  public statusCode?: number;
  
  constructor(message: string, originalError: any) {
    super(message);
    this.name = 'ApiError';
    this.originalError = originalError;
    this.statusCode = originalError?.response?.status;
  }
}

// FIXED: Updated fetchAttendancesApi to handle optional classSectionId for school-wide queries
export const fetchAttendancesApi = async (payload: AttendancePayload): Promise<Attendance[]> => {
  try {
    let url = `${ATTENDANE_API_ENDPOINT_STUDENT_ATTENDANCE}`;
    
    const filterParams = [];
    
    if (payload.schoolId) {
      filterParams.push(`schoolId|${payload.schoolId}`);
    }
    
    // Only add classSectionId filter if it's provided (for class-specific queries)
    if (payload.classSectionId) {
      filterParams.push(`classSectionId|${payload.classSectionId}`);
    }
    
    if (payload.attnDate) {
      filterParams.push(`attnDate|${payload.attnDate}`);
    }
    
    if (filterParams.length > 0) {
      url += `?filter=${filterParams.join(',')}`;
    }

    if (payload.limit) {
      const separator = filterParams.length > 0 ? '&' : '?';
      url += `${separator}limit=${payload.limit}`;
    }
    

    const response: AxiosResponse<any> = await axiosInstance.get(`${url}&limit=1000`);
    return response.data.data || response.data || [];
  } catch (error: any) {
    console.error('Error fetching Attendances:', error);
    // Handle 404 or empty results gracefully
    if (error?.response?.status === 404 || 
        error?.response?.status === 400 ||
        error?.message?.toLowerCase().includes('not found')) {
      return [];
    }
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// FIXED: Additional function to check if attendance exists for a specific date and class
export const checkAttendanceExistsApi = async (payload: AttendancePayload): Promise<{exists: boolean, data: any[], canEdit: boolean}> => {
  try {
    const attendanceData = await fetchAttendancesApi(payload);
    const exists = Array.isArray(attendanceData) && attendanceData.length > 0;
    
    const hasBeenEdited = exists && attendanceData.some((item: any) => item.updatedAt !== null);
    const canEdit = exists && !hasBeenEdited;
    
    return { exists, data: attendanceData, canEdit };
  } catch (error: any) {
    console.error('Error checking attendance existence:', error);
    return { exists: false, data: [], canEdit: false };
  }
};

export const addAttendanceApi = async (AttendanceData: Omit<Attendance, 'id'>): Promise<Attendance> => {
  try {
    const response: AxiosResponse<Attendance> = await axiosInstance.post(Attendance_API_ENDPOINT, AttendanceData);
    return response.data;
  } catch (error: any) {
    console.error('Error adding Attendance:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const updateAttendanceApi = async (Attendance: Attendance): Promise<Attendance> => {
  try {
    const response: AxiosResponse<Attendance> = await axiosInstance.patch(`${Attendance_API_ENDPOINT}/${Attendance.id}`, Attendance);
    return response.data;
  } catch (error: any) {
    console.error('Error updating Attendance:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const deleteAttendanceApi = async (id: number): Promise<number> => {
  try {
    await axiosInstance.delete(`${Attendance_API_ENDPOINT}/${id}`);
    return id;
  } catch (error: any) {
    console.error('Error deleting Attendance:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

const ensureFileReferenceObject = (fileReference: any) => {
  if (typeof fileReference === 'object' && fileReference !== null && fileReference.url) {
    return fileReference;
  }
  
  if (typeof fileReference === 'string' && fileReference.trim()) {
    return { url: fileReference.trim() };
  }
  return { url: "https://example.com/default-file.jpg" };
};
 
// Submit new attendance - FIXED VERSION
export const setAttendanceApi = async (AttendanceData: AttendanceSubmissionPayload): Promise<Attendance> => {
  try {
    // ✅ FIXED: Ensure fileReference is an OBJECT (not string) for each attendance record
    const processedData = {
      ...AttendanceData,
      attendances: AttendanceData.attendances.map(attendance => ({
        ...attendance,
        fileReference: ensureFileReferenceObject(attendance.fileReference)
      }))
    };

    console.log('Processed attendance data being sent:', JSON.stringify(processedData, null, 2));

    const response: AxiosResponse<Attendance> = await axiosInstance.post(
      ATTENDANE_API_ENDPOINT_STUDENT_ATTENDANCE_MULTIPLE, 
      processedData
    );
    return response.data;
  } catch (error: any) {
    console.error('Error adding Attendance:', error);
         
    // Preserve the original error with response data for better error handling
    if (error.response?.data?.message) {
      throw new ApiError(error.response.data.message, error);
    } else if (error.message) {
      throw new ApiError(error.message, error);
    } else {
      throw new ApiError('Failed to submit attendance', error);
    }
  }
};

// Update individual attendance records
export const updateAttendanceIndividualApi = async (attendanceId: number, attendanceData: any): Promise<Attendance> => {
  try {
    // Ensure fileReference is stringified if it's an object
    const processedData = {
      ...attendanceData,
      fileReference: typeof attendanceData.fileReference === 'object' 
        ? JSON.stringify(attendanceData.fileReference)
        : attendanceData.fileReference
    };

    const response: AxiosResponse<Attendance> = await axiosInstance.patch(
      `${ATTENDANE_API_ENDPOINT_STUDENT_ATTENDANCE}/${attendanceId}`, 
      processedData
    );
    return response.data;
  } catch (error: any) {
    console.error('Error updating individual attendance:', error);
         
    if (error.response?.data?.message) {
      throw new ApiError(error.response.data.message, error);
    } else if (error.message) {
      throw new ApiError(error.message, error);
    } else {
      throw new ApiError('Failed to update attendance', error);
    }
  }
};

// Update multiple attendance records (for bulk edit scenarios)
export const updateAttendanceMultipleApi = async (attendanceRecords: any[]): Promise<any> => {
  try {
    // Update each record individually
    const updatePromises = attendanceRecords.map(record => {
      if (record.id) {
        // Update existing record
        return updateAttendanceIndividualApi(record.id, {
          attendance: record.attendance,
          attnTypeId: record.attnTypeId,
          recordedVia: record.recordedVia,
          fileReference: record.fileReference,
          updatedBy: record.updatedBy
        });
      } else {
        // This shouldn't happen in edit mode, but handle gracefully
        throw new Error('Missing attendance ID for update');
      }
    });

    const results = await Promise.all(updatePromises);
    return { success: true, data: results };
  } catch (error: any) {
    console.error('Error updating multiple attendance records:', error);
    throw error;
  }
};